import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { CircularProgress, DialogTitle } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import {
  handleStorageAccount,
  getErrorMsg,
} from "pages/Integrations/Accounts/helpers";

import {
  Authentication,
  StorageAccount,
  StorageProvider,
  StorageProviderWithAuth,
} from "@dashboard-v3/api";
import { storageTypes } from "utils/storageProviders";
import { StorageAccountItem } from "../types";

interface Props {
  isOpen: boolean;
  excludedProviders?: Array<StorageProvider>;
  onClose: () => void;
  onAccountAdd: (change: StorageAccountItem["storageAccount"]) => void;
}

export default function AddAccountModal(props: Props) {
  const { t } = useTranslation("components");
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState<StorageProvider>(null);
  const { isOpen, onClose, onAccountAdd, excludedProviders = [] } = props;
  const options = storageTypes.filter(
    type => !excludedProviders.includes(type)
  );

  function onSuccess(account: StorageAccount, provider: StorageProvider) {
    const storageAccountUpdate = {
      id: account.id,
      authentication: "INDIVIDUAL" as Authentication,
      userId: account.user,
      provider,
    };
    onAccountAdd(storageAccountUpdate);
    onClose();
  }

  function onError(error: unknown, isCancel: boolean) {
    if (!isCancel) {
      enqueueSnackbar(getErrorMsg(error, t), {
        variant: "error",
        resumeHideDuration: 5000,
      });
      onClose();
    }
  }

  async function handleItemClick(provider: StorageProviderWithAuth) {
    if (selected && selected === provider) return;

    setSelected(provider);

    await handleStorageAccount(provider, { onSuccess, onError });

    setSelected(null);
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("storageAccounts.addModal.title")}</DialogTitle>
      <List>
        {options.map(provider => (
          <ListItem
            key={provider}
            button
            onClick={() => handleItemClick(provider)}
            disabled={selected && selected !== provider}
          >
            <ListItemText
              primary={t(`common:integration.provider.${provider}`)}
            />
            {selected === provider && <CircularProgress size={20} />}
          </ListItem>
        ))}
      </List>
    </Dialog>
  );
}
